import { useMutation, useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { away, builderNext, keptNext, safeNext } from "@/auth/session";
import { AuthLayout } from "@/components/layout";
import { RegistrationTerms } from "@/components/legal";
import {
  Alert,
  Button,
  ErrorAlert,
  Field,
  Form,
  Input,
  PasswordInput,
  PasswordRequirements,
} from "@/components/ui";
import { auth, portal, session } from "@/lib/api";
import { ApiError } from "@/lib/http";
import { MAX_PASSWORD, meetsPasswordPolicy } from "@/lib/password-policy";
import { passwordError } from "@/pages/auth/helpers";

export function RegisterPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const requirementsId = useId();
  const terms = useQuery({
    queryKey: ["legal-registration-requirements"],
    queryFn: portal.registrationRequirements,
    staleTime: 60_000,
  });
  const acceptedDocuments =
    terms.data?.documents.map(({ document_key, version }) => ({ document_key, version })) ?? [];
  const termsReady = terms.isSuccess && (acceptedDocuments.length === 0 || termsAccepted);
  const register = useMutation({
    mutationFn: () => auth.register(email.trim(), password, name.trim(), acceptedDocuments),
    onSuccess: (tokens) => {
      session.accept(tokens);
      session.announceSignIn();
      const next = params.get("next");
      // From the website builder: back there, where the new account picks or creates its business.
      const builder = builderNext(next);
      if (builder) away.go(builder);
      else navigate(next ? safeNext(next) : "/onboarding", { replace: true });
    },
  });
  // Keep the invitation (or builder hand-off) when an existing user is sent to Sign in.
  const kept = keptNext(params.get("next"));
  const signInTo = `/login${kept ? `?next=${encodeURIComponent(kept)}` : ""}`;
  const pwErr = passwordError(register.error);
  const emailTaken = register.error instanceof ApiError && register.error.status === 409;
  const passwordOk = meetsPasswordPolicy(password, email);
  return (
    <AuthLayout title="Create your account" subtitle="Start building websites with AI in minutes.">
      <Form
        className="space-y-4"
        error={register.error}
        onSubmit={(e) => {
          e.preventDefault();
          if (passwordOk && termsReady) register.mutate();
        }}
      >
        {register.error && !pwErr && !emailTaken && <ErrorAlert error={register.error} />}
        {emailTaken && (
          <Alert tone="danger">
            An account with this e-mail already exists.{" "}
            <Link to={signInTo} className="font-medium underline">
              Sign in
            </Link>
          </Alert>
        )}
        <Field label="Your name" name="name">
          {(p) => (
            <Input
              {...p}
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
              autoFocus
            />
          )}
        </Field>
        <Field label="E-mail" name="email" required>
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Field label="Password" name="password" required error={pwErr}>
          {(p) => (
            <>
              <PasswordInput
                {...p}
                aria-describedby={[p["aria-describedby"], requirementsId].filter(Boolean).join(" ")}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                maxLength={MAX_PASSWORD}
              />
              <PasswordRequirements id={requirementsId} password={password} email={email} />
            </>
          )}
        </Field>
        {terms.isPending && <p className="text-sm text-fg-muted">Loading the current terms…</p>}
        {terms.error && <ErrorAlert error={terms.error} />}
        {terms.data && (
          <RegistrationTerms
            documents={terms.data.documents}
            checked={termsAccepted}
            onChange={setTermsAccepted}
          />
        )}
        <Button
          type="submit"
          className="w-full"
          loading={register.isPending}
          disabled={!passwordOk || !termsReady}
        >
          Create account
        </Button>
      </Form>
      <p className="mt-6 text-center text-sm text-fg-muted">
        Already have an account?{" "}
        <Link to={signInTo} className="font-medium text-brand-700 hover:underline dark:text-brand-500">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
